import {CommonModule} from '@angular/common';
import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    inject,
    Input,
    OnDestroy,
    OnInit,
    output,
    Output,
    viewChildren,
} from '@angular/core';
import {NonNullableFormBuilder, ReactiveFormsModule} from '@angular/forms';
import {
    LoadComponent,
    PaginationComponent,
    applySortingToHeaders,
    SortableTableHeaderComponent,
    SortingType,
    TrueFalseComponent,
} from '@brunata-angular/ui';
import {
    comparator,
    composeComparators,
    filterNulls,
    FormValue,
    fromControlWithDebounceTime,
    keyComparator,
    TRANSLATE,
    TranslatePipe,
} from '@brunata-angular/util';
import {InputDropdownCheckboxesComponent, SelectableItem} from '@brunata-angular/ui';
import {InputTextGroupComponent} from '@brunata-angular/ui';
import {combineLatest, map, Observable, tap} from 'rxjs';
import {ConsumptionAgentTranslated} from '../consumption-agent.model';
import {
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
} from '@ng-bootstrap/ng-bootstrap';
import {ConsumptionAgentService} from '../../consumption-agent.service';
import {uniqBy} from 'lodash-es';
import {ActivatedRoute, Router} from '@angular/router';
import {
    CONSUMPTION_AGENT_TABLE_HEADERS,
    ConsumptionAgentOverview,
    ConsumptionAgentOverviewTableFilterFormGroup,
    EditAgentEvent,
    SCHEDULE_SORT_ORDER,
} from './consumption-agent-overview.model';
import {CONSUMPTION_AGENT_TYPE_INVESTIGATIONS} from '../../create-consumption-agent/consumption-agent-investigation/consumption-agent-investigation.model';

type ConsumptionAgentTableView = {
    consumptionAgents: ConsumptionAgentOverview[];
    sorting: SortingType<FormValue<ConsumptionAgentOverviewTableFilterFormGroup>>;
    collectionSize: number;
    page: number;
    pageSize: number;
};

@Component({
    selector: 'app-consumption-agent-table',
    standalone: true,
    imports: [
        CommonModule,
        TranslatePipe,
        LoadComponent,
        ReactiveFormsModule,
        SortableTableHeaderComponent,
        TrueFalseComponent,
        PaginationComponent,
        InputTextGroupComponent,
        InputDropdownCheckboxesComponent,
        NgbDropdown,
        NgbDropdownItem,
        NgbDropdownMenu,
        NgbDropdownToggle,
    ],
    templateUrl: './consumption-agent-table.component.html',
    styleUrl: './consumption-agent-table.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ConsumptionAgentTableComponent implements OnInit, OnDestroy {
    private readonly headers = viewChildren(SortableTableHeaderComponent);

    @Input() consumptionAgents: ConsumptionAgentTranslated[] = [];
    @Input() buildingNo: string | null = null;

    editConsumptionAgent = output<EditAgentEvent>();
    @Output() duplicateConsumptionAgent = new EventEmitter<string>();
    @Output() deleteConsumptionAgent = new EventEmitter<{reportId: string; agentName: string}>();

    private router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly formBuilder = inject(NonNullableFormBuilder);
    private readonly consumptionAgentService = inject(ConsumptionAgentService);
    private readonly translate = inject(TRANSLATE);

    readonly columnHeaders = CONSUMPTION_AGENT_TABLE_HEADERS;

    formGroupFilter: ConsumptionAgentOverviewTableFilterFormGroup = this.formBuilder.group({
        meterCriteriaMismatch: this.formBuilder.control<SelectableItem[]>([]),
        agentName: this.formBuilder.control<string>(''),
        investigation: this.formBuilder.control<string>(''),
        meterSuperTypeNo: this.formBuilder.control<SelectableItem[]>([]),
        usageAndUnit: this.formBuilder.control<string>(''),
        dayCount: this.formBuilder.control<string>(''),
        consumptionToMonitor: this.formBuilder.control<SelectableItem[]>([]),
        agentType: this.formBuilder.control<string>(''),
        schedule: this.formBuilder.control<SelectableItem[]>([]),
        recipients: this.formBuilder.control<string>(''),
        sendBlank: this.formBuilder.control<SelectableItem[]>([]),
        showConsumptionForMeters: this.formBuilder.control<SelectableItem[]>([]),
    });

    readonly tableSettings$ = combineLatest([
        this.consumptionAgentService.sorting.pipe(filterNulls()),
        this.consumptionAgentService.page,
        this.consumptionAgentService.pageSize,
    ]).pipe(
        map(([sorting, page, pageSize]) => ({
            sorting: sorting,
            page: page,
            pageSize: pageSize,
        })),
    );
    readonly tableData$ = combineLatest([
        fromControlWithDebounceTime(this.formGroupFilter),
        this.tableSettings$,
    ]).pipe(
        map(([filterControl, settings]) => {
            const filteredCollection = this.consumptionAgents
                .filter(
                    consumptionAgent =>
                        (filterControl.meterCriteriaMismatch == null ||
                            this.filterConsumptionAgentsFromList(
                                consumptionAgent.meterCriteriaMismatch,
                                filterControl.meterCriteriaMismatch ?? [],
                                'meterCriteriaMismatch',
                            )) &&
                        (filterControl.meterSuperTypeNo == null ||
                            this.filterConsumptionAgentsFromList(
                                consumptionAgent.meterSettings.meterSuperTypeNo,
                                filterControl.meterSuperTypeNo,
                                'meterSuperTypeNo',
                            )) &&
                        (filterControl.consumptionToMonitor == null ||
                            this.filterConsumptionAgentsFromList(
                                consumptionAgent.consumptionToMonitorTranslated,
                                filterControl.consumptionToMonitor,
                                'consumptionToMonitor',
                            )) &&
                        (filterControl.schedule == null ||
                            this.filterConsumptionAgentsFromList(
                                consumptionAgent.schedule,
                                filterControl.schedule,
                                'schedule',
                            )) &&
                        (filterControl.sendBlank == null ||
                            this.filterConsumptionAgentsFromList(
                                consumptionAgent.sendBlank,
                                filterControl.sendBlank,
                                'sendBlank',
                            )) &&
                        (filterControl.showConsumptionForMeters == null ||
                            this.filterConsumptionAgentsFromList(
                                consumptionAgent.showConsumptionForMeters,
                                filterControl.showConsumptionForMeters,
                                'showConsumptionForMeters',
                            )) &&
                        (filterControl.agentName === '' ||
                            consumptionAgent.agentName
                                .toLowerCase()
                                .includes(filterControl.agentName?.toLowerCase() ?? '')) &&
                        (filterControl.usageAndUnit === '' ||
                            filterControl.usageAndUnit == null ||
                            consumptionAgent.usageAndUnitsTranslated
                                .toLowerCase()
                                .includes(filterControl.usageAndUnit.toLowerCase())) &&
                        (filterControl.dayCount === '' ||
                            consumptionAgent.dayCount
                                .toString()
                                .includes(filterControl.dayCount?.toString() ?? '')) &&
                        (filterControl.agentType === '' ||
                            filterControl.agentType == null ||
                            this.translate(
                                CONSUMPTION_AGENT_TYPE_INVESTIGATIONS.find(
                                    invest => invest.key === consumptionAgent.agentType,
                                )?.labelKey ?? '',
                            )
                                .toLowerCase()
                                .includes(filterControl.agentType.toLowerCase())) &&
                        (filterControl.recipients === '' ||
                            filterControl.recipients == null ||
                            consumptionAgent.recipients
                                .join(', ')
                                .includes(filterControl.recipients.toLowerCase())),
                )
                .map(({meterSettings, ...agent}) => ({
                    reportId: String(agent.reportId),
                    meterCriteriaMismatch: agent.meterCriteriaMismatch,
                    agentName: agent.agentName,
                    consumptionToMonitor: agent.consumptionToMonitorTranslated,
                    dayCount: agent.dayCount,
                    meterSuperTypeNo: meterSettings.meterSuperTypeNo,
                    usageAndUnit: agent.usageAndUnitsTranslated,
                    schedule: agent.schedule,
                    recipients: agent.recipients.join(', '),
                    sendBlank: agent.sendBlank,
                    showConsumptionForMeters: agent.showConsumptionForMeters,
                    investigation: agent.investigation,
                    agentType: agent.agentType,
                }));

            return {
                ...settings,
                consumptionAgents: filteredCollection.slice(
                    (settings.page - 1) * settings.pageSize,
                    settings.page * settings.pageSize,
                ),
                collectionSize: filteredCollection.length,
            };
        }),
        tap(view =>
            setTimeout(() => {
                applySortingToHeaders(this.headers(), view.sorting);
            }, 0),
        ),
    );

    readonly view$: Observable<ConsumptionAgentTableView> = this.tableData$.pipe(
        map(tableData => ({
            ...tableData,
            consumptionAgents: this.sortConsumptionAgentTranslated(
                tableData.consumptionAgents,
                tableData.sorting,
            ),
        })),
    );

    sortConsumptionAgentTranslated(
        consumptionAgentsOverview: ConsumptionAgentOverview[],
        sorting: SortingType<FormValue<ConsumptionAgentOverviewTableFilterFormGroup>>,
    ): ConsumptionAgentOverview[] {
        switch (sorting.column) {
            case 'meterSuperTypeNo':
                return consumptionAgentsOverview.sort(
                    keyComparator(
                        consumptionAgent =>
                            this.translate(
                                `mappers_meterType.${consumptionAgent.meterSuperTypeNo}`,
                                'online',
                                'common',
                            ),
                        sorting.direction,
                    ),
                );
            case 'agentType':
                return consumptionAgentsOverview.sort(
                    comparator({
                        selector: consumptionAgent =>
                            this.translate(
                                CONSUMPTION_AGENT_TYPE_INVESTIGATIONS.find(
                                    invest => invest.key === consumptionAgent.agentType,
                                )?.labelKey ?? '',
                            ),
                        order: sorting.direction,
                    }),
                );
            case 'consumptionToMonitor':
                return consumptionAgentsOverview.sort(
                    comparator({
                        selector: consumptionAgent =>
                            this.translate(
                                'consumption-agents_consumptionType_' +
                                    consumptionAgent.consumptionToMonitor,
                            ),
                        order: sorting.direction,
                    }),
                );

            case 'schedule':
                return consumptionAgentsOverview.sort(
                    comparator({
                        selector: consumptionAgent =>
                            this.translate(
                                'reportagent_' + consumptionAgent.schedule,
                                'online',
                                'reportagent',
                            ),
                        order: sorting.direction,
                    }),
                );
            default:
                return consumptionAgentsOverview.sort(
                    comparator({
                        selector: consumptionAgent => consumptionAgent[sorting.column],
                        order: sorting.direction,
                    }),
                );
        }
    }

    ngOnInit(): void {
        this.formGroupFilter.patchValue({
            meterCriteriaMismatch: [
                {
                    name: this.translate('consumption-agents_Meter-list-must-be-updated'),
                    selected: false,
                },
                {
                    name: this.translate('common_All_connected_devices_supported'),
                    selected: false,
                },
            ],
            meterSuperTypeNo: this.getUniqueValuesForLists(
                this.consumptionAgents,
                'meterSuperTypeNo',
            ),
            consumptionToMonitor: this.getUniqueValuesForLists(
                this.consumptionAgents,
                'consumptionToMonitor',
            ),
            schedule: this.getUniqueValuesForLists(this.consumptionAgents, 'schedule'),
            sendBlank: this.createTrueFalseOptions(),
            showConsumptionForMeters: this.createTrueFalseOptions(),
        });
    }

    ngOnDestroy(): void {
        this.consumptionAgentService.setFilterColumns({
            agentName: this.formGroupFilter.value.agentName ?? '',
            investigation: this.formGroupFilter.value.investigation ?? '',
            meterCriteriaMismatch: this.formGroupFilter.value.meterCriteriaMismatch ?? [],
            meterSuperTypeNo: this.formGroupFilter.value.meterSuperTypeNo ?? [],
            usageAndUnit: this.formGroupFilter.value.usageAndUnit ?? '',
            dayCount: this.formGroupFilter.value.dayCount ?? '',
            consumptionToMonitor: this.formGroupFilter.value.consumptionToMonitor ?? [],
            agentType: this.formGroupFilter.value.agentType ?? '',
            schedule: this.formGroupFilter.value.schedule ?? [],
            recipients: this.formGroupFilter.value.recipients ?? '',
            sendBlank: this.formGroupFilter.value.sendBlank ?? [],
            showConsumptionForMeters: this.formGroupFilter.value.showConsumptionForMeters ?? [],
        });
    }

    setPage(page: number): void {
        this.consumptionAgentService.page.next(page);
    }

    setPageSize(pageSize: number): void {
        this.consumptionAgentService.pageSize.next(pageSize);
    }

    onSort(sorting: SortingType<FormValue<ConsumptionAgentOverviewTableFilterFormGroup>>): void {
        applySortingToHeaders(this.headers(), sorting);
        this.consumptionAgentService.updateSorting(sorting);
    }

    editAgent(reportId: string, agentName: string): void {
        this.editConsumptionAgent.emit({reportId, agentName});
    }

    deleteAgent({reportId, agentName}: ConsumptionAgentOverview): void {
        this.deleteConsumptionAgent.emit({reportId, agentName});
    }

    duplicateAgent(reportId: string): void {
        this.duplicateConsumptionAgent.emit(reportId);
    }

    private getUniqueValuesForLists(
        consumptionAgent: ConsumptionAgentTranslated[],
        key: keyof FormValue<ConsumptionAgentOverviewTableFilterFormGroup>,
    ): SelectableItem[] {
        const valueList = new Array<SelectableItem>();
        consumptionAgent.forEach(agent => {
            if (key === 'meterSuperTypeNo') {
                const translatedValue = this.translate(
                    'mappers_meterType.' + agent.meterSettings.meterSuperTypeNo,
                    'online',
                    'common',
                );
                valueList.push({name: translatedValue, selected: false});
            }
            if (key === 'consumptionToMonitor') {
                valueList.push({
                    name: agent.consumptionToMonitorTranslated,
                    selected: false,
                });
            }

            if (key === 'schedule') {
                const translatedValue = this.translate(
                    'reportagent_' + agent.schedule,
                    'online',
                    'reportagent',
                );
                valueList.push({
                    name: translatedValue,
                    selected: false,
                    sortOrder: SCHEDULE_SORT_ORDER.find(it => it.name === agent.schedule)
                        ?.sortOrder,
                });
            }
        });
        return uniqBy(valueList, item => item.name).sort(
            composeComparators([
                keyComparator(item => item.sortOrder),
                keyComparator(item => item.name),
            ]),
        );
    }

    private filterConsumptionAgentsFromList(
        value: string | number | boolean | null,
        selectableItem: SelectableItem[],
        parameter: string,
    ): boolean {
        const valuesToFilter = selectableItem
            .filter(it => it.selected)
            .map(it => it.name.toString());
        if (valuesToFilter.length === 0) {
            return true;
        }
        switch (parameter) {
            case 'meterCriteriaMismatch':
                return valuesToFilter.includes(
                    this.translate(
                        value === true
                            ? 'consumption-agents_Meter-list-must-be-updated'
                            : 'common_All_connected_devices_supported',
                    ),
                );
            case 'meterSuperTypeNo':
                return valuesToFilter.includes(
                    this.translate('mappers_meterType.' + value, 'online', 'common'),
                );
            case 'consumptionToMonitor':
                return valuesToFilter.includes(value != null ? value.toString() : '');
            case 'schedule':
                return valuesToFilter.includes(
                    this.translate('reportagent_' + value, 'online', 'reportagent'),
                );
            case 'sendBlank':
            case 'showConsumptionForMeters':
                return valuesToFilter.includes(
                    this.translate(
                        value === true ? 'actions_yes' : 'actions_no',
                        'online',
                        'common',
                    ),
                );
            default:
                return true;
        }
    }

    private createTrueFalseOptions(): SelectableItem[] {
        return [
            {
                name: this.translate('actions_yes', 'online', 'common'),
                selected: false,
            },
            {
                name: this.translate('actions_no', 'online', 'common'),
                selected: false,
            },
        ];
    }

    protected navigateToResults(consumptionAgent: ConsumptionAgentOverview): void {
        void this.router.navigate(['./', consumptionAgent.reportId], {
            relativeTo: this.activatedRoute,
            queryParams: {
                reportId: consumptionAgent.reportId,
                consumptionToMonitor: consumptionAgent.consumptionToMonitor,
                agentType: consumptionAgent.agentType,
            },
        });
    }
}
